import { AddCircleIcon, CoinPlusIcon } from '@synergy/theme-next';
import { DocumentStatus } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolSplitButton } from '@synergy/components-next';
import { Stack } from '@mui/material';
import { usePaloThemeGuard } from '@synergy/theme-palo';
import { useState } from 'react';

import { PaloAddPositionsDialog } from '../../../common/components/palo/palo-add-positions-dialog';
import { PurchaseOrder } from '../models/purchaseOrder';
import { PurchaseOrderGenericItemType } from '../models/purchase-order-generic-item';
import { usePurchaseOrderDetailContext } from '../containers/context/purchase-order-context';

export interface PaloPurchaseOrderPositionToolsProps {
    entity?: Partial<PurchaseOrder>;
    unlocked: boolean;
    /** Puts an empty row under the table, which then carries the article search. */
    onAddRow?: () => void;
}

/** Position toolbar of the purchase-order editor (split off the legacy positions footer). */
export const PaloPurchaseOrderPositionTools = (props: PaloPurchaseOrderPositionToolsProps): JSX.Element => {
    const { entity, unlocked, onAddRow } = props;
    const [showAddMultiple, setShowAddMultiple] = useState(false);
    const {
        setSelectedProduct,
        setShowAddPositionModal,
        setShowGenericItemModal,
        supplierAccountId,
        supplierAccountName,
    } = usePurchaseOrderDetailContext();
    usePaloThemeGuard('PaloPurchaseOrderPositionTools');

    const isCompleted =
        entity?.status === DocumentStatus.Completed ||
        entity?.status === DocumentStatus.CompletedWithTransition ||
        entity?.status === DocumentStatus.CompletedWithoutTransition ||
        entity?.status === DocumentStatus.Canceled;
    const disabled = isCompleted && !unlocked;

    /**
     * The supplier the head holds right now; the saved one only where the head
     * is not on screen (Prüfbericht 28.09.2026, B1/B4).
     */
    const supplierId = undefined !== supplierAccountId ? supplierAccountId ?? undefined : entity?.supplierAccount?.id;
    const supplierIds = supplierId ? [supplierId] : undefined;
    const supplierName = undefined !== supplierAccountId ? supplierAccountName : entity?.supplierAccount?.name;

    return (
        <Stack direction="row" gap={2} alignItems="flex-end" sx={{ flex: 1, minWidth: 530, maxWidth: 700 }}>
            {/* An empty row first, the article searched for in the row — and
                behind the arrow the free position, which has no article to
                find (#2208). */}
            <PoolSplitButton
                variant="outlined"
                color="inherit"
                preSelectedIndex={0}
                menuItems={[
                    {
                        label: <FormattedMessage id="app.positions.addRow" />,
                        displayLabel: <FormattedMessage id="app.positions.addRow" />,
                        icon: <AddCircleIcon />,
                        onClick: () => onAddRow?.(),
                    },
                    {
                        label: <FormattedMessage id="app.purchaseOrders.detail.addFreePosition" />,
                        displayLabel: <FormattedMessage id="app.purchaseOrders.detail.addFreePositionShort" />,
                        icon: <CoinPlusIcon />,
                        onClick: () => setShowAddPositionModal?.(true),
                    },
                ]}
                sx={{ flexShrink: 0 }}
                disabled={disabled}
            />
            {/* The article search lives in here now, the way ERPNext keeps it
                in "Add multiple": pick, the row appears, pick the next — the
                dialog stays open (#2208). */}
            <PoolButton
                variant="outlined"
                color="inherit"
                startIcon={<AddCircleIcon />}
                disabled={disabled}
                onClick={() => setShowAddMultiple(true)}
            >
                <FormattedMessage id="app.positions.addMultiple" />
            </PoolButton>
            <PaloAddPositionsDialog
                open={showAddMultiple}
                onClose={() => setShowAddMultiple(false)}
                onSelect={product => setSelectedProduct?.(product)}
                supplierIds={supplierIds}
                supplierName={supplierName}
                supplierMissing={!supplierId}
                includeNormArticles={true}
            />
        </Stack>
    );
};
